/** A string drawn as a QR code, in the browser, as inline SVG (ADR-0059, option C).
 *
 * Built for one input, the TOTP provisioning URI, and **that URI is a credential**: it carries the
 * shared secret. Four properties follow, and each is asserted by a test named after the thing it
 * protects (`QrCode.test.tsx`).
 *
 * **Nothing leaves the page.** The encoder is a pure function from a string to a matrix. There is no
 * `fetch`, no `<img src>`, no query string — a hosted QR service would put the secret in somebody
 * else's access log, and an `<img>` would also need `img-src` widened. SVG *elements* are not a
 * resource load, which is why the CSP needs no change.
 *
 * **The value is never read back out.** It is not logged, not put in an attribute, not in the
 * accessible name. `label` is the caller's words and the component refuses to guess one from the
 * value, because the obvious default is the value.
 *
 * **It fails by rendering nothing.** Two inputs are refused rather than drawn: one too long for the
 * largest symbol, and one with a character outside ASCII. The second matters more than it looks —
 * the library's default byte mode is Latin-1, so a wider character would not fail, it would scan
 * into a *different* string, and an authenticator enrolled against a secret nobody holds fails at
 * the next sign-in rather than now. That is the failure ADR-0059 refused option D over. A provisioning
 * URI is ASCII by construction (the label is percent-encoded), so this costs nothing real. The caller
 * keeps the typed secret on screen regardless, so "nothing" is a degraded screen and not a blocked one.
 *
 * **Always dark on light.** A scanner expects it and a dark theme would invert it, so the two
 * colours are their own tokens (`--qr-dark`, `--qr-light`) rather than the surface's. The quiet zone
 * is four modules, the specification's minimum, and is part of the drawn rectangle: without it the
 * code merges into whatever the card's background is.
 */

import qrcode from "qrcode-generator";
import { useMemo } from "react";

import styles from "./QrCode.module.css";

/** The specification's quiet zone, in modules. */
const QUIET = 4;

/** Whether every character is ASCII. A loop rather than a regex over `\x00-\x7f`, which
 * `no-control-regex` refuses and which is not worth a disable for. */
function isAscii(value: string): boolean {
  for (let i = 0; i < value.length; i++) if (value.charCodeAt(i) > 0x7f) return false;
  return true;
}

/** The dark modules as one SVG path, or `null` when the value cannot be drawn faithfully.
 *
 * One path of horizontal runs rather than a `<rect>` per module: a version-7 symbol is ~2,000
 * modules and a node each is a lot of DOM for something that is a picture. */
function pathFor(value: string): { size: number; d: string } | null {
  if (value.length === 0 || !isAscii(value)) return null;
  const code = qrcode(0, "M");
  try {
    code.addData(value, "Byte");
    code.make();
  } catch {
    // Deliberately not logged, and the error is not kept: the library's message can include the
    // length it was given, and the input is a credential.
    return null;
  }
  const count = code.getModuleCount();
  const runs: string[] = [];
  for (let row = 0; row < count; row++) {
    let col = 0;
    while (col < count) {
      if (!code.isDark(row, col)) {
        col += 1;
        continue;
      }
      const start = col;
      while (col < count && code.isDark(row, col)) col += 1;
      runs.push(`M${start + QUIET} ${row + QUIET}h${col - start}v1h-${col - start}z`);
    }
  }
  return { size: count + QUIET * 2, d: runs.join("") };
}

interface QrCodeProps {
  /** What the code encodes. Never rendered as text and never echoed into an attribute. */
  value: string;
  /** The image's accessible name. **Must not contain `value` or anything derived from it**; the
   * caller supplies it for that reason. */
  label: string;
  testId?: string;
}

export function QrCode({ value, label, testId }: QrCodeProps) {
  const drawn = useMemo(() => pathFor(value), [value]);
  if (!drawn) return null;
  return (
    <svg
      className={styles.code}
      viewBox={`0 0 ${drawn.size} ${drawn.size}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
      data-testid={testId}
    >
      <rect className={styles.light} width={drawn.size} height={drawn.size} />
      <path className={styles.dark} d={drawn.d} />
    </svg>
  );
}
